/**
 * React-agnostic keyboard shortcuts management system.
 * Handles registration, parsing, and execution of keyboard shortcuts.
 */

import { parseHotkey } from './parse-hotkey';
import type { KeyboardShortcutsConfig, KeyStep, RegisteredShortcut, SequenceState, ShortcutDefinition } from './types';

/**
 * Core keyboard shortcuts management class.
 * Provides a React-agnostic API for managing keyboard shortcuts.
 */
export class KeyboardShortcuts {
  private readonly config: Required<KeyboardShortcutsConfig>;
  private readonly shortcuts: Map<string, RegisteredShortcut> = new Map();
  private readonly enabledScopes: Set<string> = new Set(['global']);
  private readonly listeners = new Set<() => void>();
  private activeShortcutsSnapshot: ShortcutDefinition[] | null = null;
  private sequenceState: SequenceState = { type: 'idle' };
  private isListening = false;

  constructor(config: KeyboardShortcutsConfig = {}) {
    this.config = {
      usePhysicalKeys: config.usePhysicalKeys ?? true,
      respectFormFocus: config.respectFormFocus ?? true,
      sequenceTimeout: config.sequenceTimeout ?? 1000,
    };

    this.startListening();
  }

  /**
   * Register shortcuts with optional scope.
   */
  register(shortcuts: ShortcutDefinition[], scope = 'global'): void {
    for (const shortcut of shortcuts) {
      const parsed = parseHotkey(shortcut.hotkey, this.config.usePhysicalKeys);
      const registered: RegisteredShortcut = {
        ...shortcut,
        parsed,
        scope: shortcut.scope ?? scope,
        enabled: shortcut.enabled ?? true,
      };

      this.shortcuts.set(shortcut.id, registered);
    }
    if (shortcuts.length > 0) {
      this.emitActiveShortcutsChange();
    }
  }

  /**
   * Unregister all shortcuts in a scope.
   */
  unregister(scope: string): void {
    const toRemove: string[] = [];
    for (const [id, shortcut] of this.shortcuts) {
      if (shortcut.scope === scope) {
        toRemove.push(id);
      }
    }
    if (toRemove.length === 0) {
      return;
    }
    for (const id of toRemove) {
      this.shortcuts.delete(id);
    }
    this.emitActiveShortcutsChange();
  }

  /**
   * Unregister a specific shortcut by ID.
   */
  unregisterById(shortcutId: string): void {
    if (this.shortcuts.delete(shortcutId)) {
      this.emitActiveShortcutsChange();
    }
  }

  /**
   * Enable a scope.
   */
  enableScope(scope: string): void {
    if (this.enabledScopes.has(scope)) {
      return;
    }
    this.enabledScopes.add(scope);
    this.emitActiveShortcutsChange();
  }

  /**
   * Disable a scope.
   */
  disableScope(scope: string): void {
    if (this.enabledScopes.delete(scope)) {
      this.emitActiveShortcutsChange();
    }
  }

  /**
   * Get all registered shortcuts with their current enabled state.
   */
  readonly getActiveShortcuts = (): ShortcutDefinition[] => {
    if (this.activeShortcutsSnapshot === null) {
      const shortcuts: ShortcutDefinition[] = [];
      for (const shortcut of this.shortcuts.values()) {
        shortcuts.push({ ...shortcut, enabled: this.isShortcutEnabled(shortcut) });
      }
      this.activeShortcutsSnapshot = shortcuts;
    }
    return this.activeShortcutsSnapshot;
  };

  readonly subscribe = (onStoreChange: () => void): (() => void) => {
    this.listeners.add(onStoreChange);
    return () => {
      this.listeners.delete(onStoreChange);
    };
  };

  /**
   * Handle keyboard events.
   */
  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    const keyStep = this.extractKeyStep(event);

    // Skip if in form field and respectFormFocus is enabled
    // Allow shortcuts with modifiers (meta, ctrl, alt) as they're clearly command shortcuts
    const hasModifiers = keyStep.modifiers.meta || keyStep.modifiers.ctrl || keyStep.modifiers.alt;
    if (this.config.respectFormFocus && this.isFormField(event.target) && !hasModifiers) {
      this.resetSequence();
      return;
    }

    // Get active shortcuts
    const activeShortcuts = Array.from(this.shortcuts.values()).filter((s) => this.isShortcutEnabled(s));

    // Handle based on current state
    if (this.sequenceState.type === 'idle') {
      this.handleIdleState(keyStep, activeShortcuts, event);
    } else {
      this.handleSequenceState(keyStep, event);
    }
  };

  private isShortcutEnabled(shortcut: ShortcutDefinition): boolean {
    return this.enabledScopes.has(shortcut.scope ?? 'global') && shortcut.enabled === true;
  }

  /**
   * Handle key press in idle state.
   */
  private handleIdleState(keyStep: KeyStep, shortcuts: RegisteredShortcut[], event: KeyboardEvent): void {
    // Check for exact combination match
    const combinationMatch = shortcuts.find(
      (s) => s.parsed.type === 'combination' && this.matchesKeyStep(keyStep, s.parsed.steps),
    );

    if (combinationMatch) {
      event.preventDefault();
      combinationMatch.handler();
      return;
    }

    // Check for sequence start
    const sequenceCandidates = shortcuts.filter(
      (s) => s.parsed.type === 'sequence' && this.matchesKeyStep(keyStep, s.parsed.steps[0]),
    );

    if (sequenceCandidates.length > 0) {
      event.preventDefault();
      this.startSequence([keyStep], sequenceCandidates);
    }
  }

  /**
   * Handle key press during sequence.
   */
  private handleSequenceState(keyStep: KeyStep, event: KeyboardEvent): void {
    if (this.sequenceState.type !== 'sequence') {
      return;
    }

    const { prefix, candidates } = this.sequenceState;
    const nextStepIndex = prefix.length;

    // Find matching candidates
    const matches = candidates.filter(
      (c) =>
        c.parsed.type === 'sequence' &&
        c.parsed.steps.length > nextStepIndex &&
        this.matchesKeyStep(keyStep, c.parsed.steps[nextStepIndex]),
    );

    if (matches.length === 0) {
      // No matches - reset sequence
      this.resetSequence();
      // Re-process key in idle state (might start new sequence)
      this.handleIdleState(
        keyStep,
        Array.from(this.shortcuts.values()).filter((s) => this.isShortcutEnabled(s)),
        event,
      );
      return;
    }

    event.preventDefault();
    const newPrefix = [...prefix, keyStep];

    // Check for exact match
    const exactMatch = matches.find((m) => m.parsed.type === 'sequence' && m.parsed.steps.length === newPrefix.length);
    if (exactMatch) {
      exactMatch.handler();
      this.resetSequence();
      return;
    }

    // Continue sequence with remaining candidates
    this.startSequence(newPrefix, matches);
  }

  /**
   * Start or continue a sequence.
   */
  private startSequence(prefix: KeyStep[], candidates: RegisteredShortcut[]): void {
    this.clearSequenceTimer();

    const timeout = setTimeout(() => {
      this.resetSequence();
    }, this.config.sequenceTimeout);

    this.sequenceState = {
      type: 'sequence',
      prefix,
      candidates,
      timeout,
    };
  }

  /**
   * Reset sequence state.
   */
  private resetSequence(): void {
    this.clearSequenceTimer();
    this.sequenceState = { type: 'idle' };
  }

  /**
   * Clear sequence timer.
   */
  private clearSequenceTimer(): void {
    if (this.sequenceState.type === 'sequence' && this.sequenceState.timeout) {
      clearTimeout(this.sequenceState.timeout);
    }
  }

  /**
   * Extract key step from keyboard event.
   */
  private extractKeyStep(event: KeyboardEvent): KeyStep {
    const key = this.config.usePhysicalKeys ? event.code : event.key;

    return {
      key,
      modifiers: {
        meta: event.metaKey,
        ctrl: event.ctrlKey,
        shift: event.shiftKey,
        alt: event.altKey,
      },
    };
  }

  /**
   * Check if key step matches expected.
   */
  private matchesKeyStep(actual: KeyStep, expected: KeyStep): boolean {
    return (
      actual.key === expected.key &&
      actual.modifiers.meta === expected.modifiers.meta &&
      actual.modifiers.ctrl === expected.modifiers.ctrl &&
      actual.modifiers.shift === expected.modifiers.shift &&
      actual.modifiers.alt === expected.modifiers.alt
    );
  }

  /**
   * Check if target is a form field.
   */
  private isFormField(target: EventTarget | null): boolean {
    if (!target || !(target instanceof HTMLElement)) {
      return false;
    }

    // In some contexts, like when a listbox is managed by an autocomplete,
    // the event target is not the search input, but the listbox itself.
    // We also need to check for combobox (used by react-select and similar),
    // menu and menuitem (used by React Aria Menu components inside Autocomplete).
    const isPotentialKeyboardTarget = ['listbox', 'option', 'combobox', 'menu', 'menuitem'].includes(
      target.getAttribute('role') ?? '',
    );

    const isFormElement =
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      target.isContentEditable;

    return isFormElement || isPotentialKeyboardTarget;
  }

  /**
   * Start listening for keyboard events.
   */
  private startListening(): void {
    if (this.isListening) {
      return;
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', this.handleKeyDown);
      this.isListening = true;
    }
  }

  /**
   * Stop listening for keyboard events.
   */
  stopListening(): void {
    if (!this.isListening) {
      return;
    }

    if (typeof document !== 'undefined') {
      document.removeEventListener('keydown', this.handleKeyDown);
      this.isListening = false;
      this.clearSequenceTimer();
    }
  }

  /**
   * Cleanup resources.
   */
  destroy(): void {
    this.stopListening();
    this.shortcuts.clear();
    this.enabledScopes.clear();
    this.resetSequence();
    this.emitActiveShortcutsChange();
  }

  private emitActiveShortcutsChange(): void {
    this.activeShortcutsSnapshot = null;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
